import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { maskitoNumberOptionsGenerator, maskitoParseNumber } from '@maskito/kit';
import { maskitoTransform } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';
import { NgIf } from '@angular/common';

@Component({
  selector: 'app-recurring-currency-input',
  templateUrl: './recurring-currency-input.component.html',
  styleUrls: ['./recurring-currency-input.component.scss'],
  standalone: true,
  imports: [MaskitoDirective, NgIf],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RecurringCurrencyInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecurringCurrencyInputComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  @ViewChild('inputEl') private inputEl: ElementRef<HTMLInputElement> | undefined;

  @Input() controlId: string | null = null;
  @Input() error: boolean = false;
  @Output() valueChange = new EventEmitter<number | null>();

  @Input() set frequency(val: 'monthly' | 'annual') {
    this._frequency = val;
    this.cdr.markForCheck();
  }
  private _frequency: 'monthly' | 'annual' = 'monthly';

  public readonly mask = maskitoNumberOptionsGenerator({
    precision: 2,
    thousandSeparator: ',',
    decimalSeparator: '.',
    min: 0
  });

  public value: string = '';
  public disabled: boolean = false;

  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  get suffix(): string {
    return this._frequency === 'monthly' ? '/mo' : '/yr';
  }

  get placeholder(): string {
    return '0.00 ' + this.suffix;
  }

  public focusInput(): void {
    const input = this.inputEl;
    if (!input) return;
    input.nativeElement.focus();
  }

  public writeValue(value: number | null): void {
    this.value = value === null ? '' : maskitoTransform(value.toFixed(2), this.mask);
    this.cdr.markForCheck();
  }

  public registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value;
    this.value = inputValue;

    const numericValue = maskitoParseNumber(inputValue);
    this.valueChange.emit(isNaN(numericValue) ? null : numericValue);
    this.onChange(isNaN(numericValue) ? null : numericValue);
  }

  public handleBlur(): void {
    this.onTouched();
    const numericValue = maskitoParseNumber(this.value);
    if (!isNaN(numericValue)) {
      this.value = maskitoTransform(numericValue.toFixed(2), this.mask);
    } else {
      this.value = '';
    }
    this.cdr.markForCheck();
  }
}
